import imageCompression from 'browser-image-compression';
import { storage } from '../firebase/config.ts';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const compressionOptions = {
  maxSizeMB: 0.5,           // Target size: 500KB (increased from 200KB)
  maxWidthOrHeight: 1600,   // Max dimension 1600px
  initialQuality: 0.95,     // Start with 95% quality (increased from 92%)
  useWebWorker: true,       // Use web worker for better performance
  preserveExif: true,       // Keep image metadata
  maxIteration: 4,          // Up to 4 compression attempts (increased from 3)
  fileType: 'image/jpeg',   // Convert all images to JPEG
  minQuality: 0.8          // Don't go below 80% quality (increased from 70%)
};

export const imageService = {
  /**
   * Compresses an image file according to standard settings
   */
  async compressImage(file: File): Promise<File> {
    try {
      if (!file.type.startsWith('image/')) {
        throw new Error('Not an image file');
      }

      // First try with standard compression
      try {
        const compressedFile = await imageCompression(file, compressionOptions);
        return new File([compressedFile], file.name, {
          type: 'image/jpeg'
        });
      } catch (error) {
        console.warn('Standard compression failed, trying with more lenient settings:', error);
        
        // If standard compression fails, try with more lenient settings
        const lenientOptions = {
          ...compressionOptions,
          maxSizeMB: 1.0,        // Allow up to 1MB
          initialQuality: 0.98,   // Start with very high quality
          minQuality: 0.9,        // Don't go below 90% quality
          maxIteration: 2         // Only try twice
        };
        
        const compressedFile = await imageCompression(file, lenientOptions);
        return new File([compressedFile], file.name, {
          type: 'image/jpeg'
        });
      }
    } catch (error) {
      console.error('Error compressing image:', error);
      throw error;
    }
  },

  /**
   * Fetches an image from a URL and returns it as a compressed File
   */
  async fetchAndCompressImage(url: string, index: number, retryCount = 0): Promise<{ url: string; file: File }> {
    try {
      // Handle tel-aviv.gov.il URLs differently
      const isTelAvivUrl = url.includes('tel-aviv.gov.il');
      const isMevalimUrl = url.includes('mevalim.co.il');

      // For Mevalim URLs, try using an img element to load the image first
      if (isMevalimUrl) {
        try {
          const img = new Image();
          const imageLoadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
            img.onload = () => resolve(img);
            img.onerror = reject;
            // Allow cross-origin loading
            img.crossOrigin = "anonymous";
            img.src = url;
          });

          // Wait for image to load
          const loadedImg = await imageLoadPromise;
          
          // Convert to blob using canvas
          const canvas = document.createElement('canvas');
          canvas.width = loadedImg.width;
          canvas.height = loadedImg.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Could not get canvas context');
          
          ctx.drawImage(loadedImg, 0, 0);
          const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (blob) => {
                if (blob) resolve(blob);
                else reject(new Error('Failed to create blob'));
              },
              'image/jpeg',
              0.95
            );
          });

          const originalFile = new File([blob], `image_${Date.now()}_${index}.jpg`, { 
            type: 'image/jpeg'
          });
          const compressedFile = await this.compressImage(originalFile);
          return { url, file: compressedFile };
        } catch (error) {
          console.log('Direct image loading failed for Mevalim image, trying proxy...', error);
        }
      }

      const fetchUrl = (isTelAvivUrl || isMevalimUrl) && process.env.NODE_ENV === 'development'
        ? `http://localhost:8080/proxy?url=${encodeURIComponent(url)}`
        : url;

      // Determine if we need to use the proxy for this URL
      const needsProxy = !isTelAvivUrl && (
        url.includes('levontin7.com') ||
        url.includes('facebook.com') ||
        url.includes('fbcdn.net') ||
        url.includes('mevalim.co.il')
      );

      const proxyUrl = needsProxy
        ? `http://localhost:8080/proxy?url=${encodeURIComponent(url)}`
        : fetchUrl;

      const fetchOptions: RequestInit = {
        mode: 'cors',
        credentials: 'omit',
        headers: isTelAvivUrl ? {
          'Accept': '*/*',
          'Accept-Language': 'en-US,en;q=0.9,he;q=0.8',
          'Origin': 'https://www.tel-aviv.gov.il',
          'Referer': 'https://www.tel-aviv.gov.il/',
          'Sec-Fetch-Dest': 'image',
          'Sec-Fetch-Mode': 'cors',
          'Sec-Fetch-Site': 'same-site',
          'Pragma': 'no-cache',
          'Cache-Control': 'no-cache'
        } : isMevalimUrl ? {
          'Accept': 'image/*',
          'Accept-Language': 'en-US,en;q=0.9,he;q=0.8',
          'Origin': 'https://tickets.mevalim.co.il',
          'Referer': 'https://tickets.mevalim.co.il/'
        } : undefined
      };

      console.log(`Fetching image from ${proxyUrl} with options:`, fetchOptions);
      
      // Try to fetch the image
      const response = await fetch(proxyUrl, fetchOptions);
      
      if (!response.ok) {
        throw new Error(`GET request failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('Received empty blob');
      }

      console.log(`Successfully fetched image: ${url}`, {
        size: blob.size,
        type: blob.type
      });

      // Validate image data using canvas
      try {
        const img = new Image();
        const imageLoadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
          img.onload = () => resolve(img);
          img.onerror = (e) => reject(new Error('Failed to load image: ' + e));
          img.src = URL.createObjectURL(blob);
        });

        const loadedImg = await imageLoadPromise;
        
        // Convert to validated JPEG using canvas
        const canvas = document.createElement('canvas');
        canvas.width = loadedImg.width;
        canvas.height = loadedImg.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas context');
        
        ctx.drawImage(loadedImg, 0, 0);
        const validatedBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Failed to create blob'));
            },
            'image/jpeg',
            0.95
          );
        });

        // Clean up object URL
        URL.revokeObjectURL(img.src);

        const originalFile = new File([validatedBlob], `image_${Date.now()}_${index}.jpg`, { 
          type: 'image/jpeg'
        });

        // Compress the validated image
        const compressedFile = await this.compressImage(originalFile);
        
        return {
          url,
          file: compressedFile
        };
      } catch (error) {
        console.error('Error validating image:', error);
        throw error;
      }
    } catch (error) {
      console.error(`Error fetching and compressing image (attempt ${retryCount + 1}):`, error);
      
      // If we haven't tried the proxy yet and got a CORS error, try using the proxy
      if (retryCount === 0 && error instanceof TypeError && error.message.includes('CORS')) {
        console.log('Retrying with proxy...');
        return this.fetchAndCompressImage(
          `http://localhost:8080/proxy?url=${encodeURIComponent(url)}`,
          index,
          retryCount + 1
        );
      }
      
      throw error;
    }
  },

  /**
   * Uploads a compressed image to Firebase Storage and returns the URL
   */
  async uploadImage(file: File, path: string): Promise<string> {
    try {
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  /**
   * Process multiple images: compress and upload them
   */
  async processImages(files: File[], eventId: string): Promise<string[]> {
    const uploadedUrls: string[] = [];
    
    for (let i = 0; i < files.length; i++) {
      try {
        const compressedFile = await this.compressImage(files[i]);
        const url = await this.uploadImage(
          compressedFile,
          `events_images/${eventId}/${compressedFile.name}`
        );
        uploadedUrls.push(url);
      } catch (error) {
        console.error(`Error processing image ${i}:`, error);
      }
    }
    
    return uploadedUrls;
  },

  /**
   * Fetch and process multiple images from URLs
   */
  async fetchAndProcessImages(urls: string[]): Promise<{ url: string; file: File }[]> {
    if (!urls || urls.length === 0) return [];
    
    const processedImages: { url: string; file: File }[] = [];
    
    for (let i = 0; i < urls.length; i++) {
      try {
        const processed = await this.fetchAndCompressImage(urls[i], i);
        processedImages.push(processed);
      } catch (error) {
        console.error(`Error processing image URL ${i}:`, error);
      }
    }
    
    return processedImages;
  }
}; 